import { useCallback, useEffect, useRef, useState, type PointerEvent, type MouseEvent } from 'react';
import { useAccount } from '../auth/useAccount';
import { useToken } from '../auth/useToken';
import { Icon } from './Icon';
import {
  deleteConversation,
  groupConversations,
  listConversations,
  type ConversationSummary,
} from '../lib/conversations';

/**
 * Persistent chat-history sidebar — the left rail of the redesigned chat
 * shell.
 *
 * Render order (per the iter-2 mock):
 *   .side-head        — brand + Online dot + green new-chat + collapse chevron
 *   .side-search      — magnifier-prefixed search input ("Search chats")
 *   .side-sessions    — scroll region: TODAY / YESTERDAY / LAST 7 DAYS / EARLIER
 *                       groups OR the no-chats-yet empty state when there is
 *                       no history.
 *   .side-foot        — avatar + display name + settings cog + sign-out
 *   .side-resize      — 4 px drag handle on the right edge
 *
 * Iter-2 collapsed the duplicate "Saved prompts" surface that previously
 * lived in this rail — those now live exclusively in the empty-state grid in
 * the main pane (see `EmptyState.tsx`). The Sidebar's responsibility is
 * past-facing (history navigation), the grid's is future-facing (action).
 */
export interface SidebarProps {
  activeThreadKey: string | null;
  onNewChat: () => void;
  onResumeSession: (threadKey: string) => void;
  onOpenSettings: () => void;
  onSignOut: () => void;

  /** Visual state */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  mobile?: boolean;
  onClose?: () => void;

  /** Desktop sidebar width (px). Bounded 220..480 by the resize logic. */
  width?: number;
  onResize?: (px: number) => void;

  /** Status dot color: 'online' (green) | 'rate-limited' (amber) | 'paused' (red). */
  status?: 'online' | 'rate-limited' | 'paused';

  /**
   * Parent-controlled refresh signal. Bumping this number re-runs the
   * conversations fetch even when activeThreadKey + query are unchanged
   * — used by ChatScreen to force a refresh after a /ask turn finishes
   * so newly-created threads + freshly-generated titles appear in the
   * sidebar without a page reload.
   */
  refreshTick?: number;

  /**
   * Fired after a successful DELETE /conversations/{threadKey}. ChatScreen
   * uses this to reset the main pane to the empty state when the user
   * deletes the conversation that is currently being displayed.
   */
  onConversationDeleted?: (threadKey: string) => void;
}

const SEARCH_DEBOUNCE_MS = 200;

function sessionLabel(c: ConversationSummary): string {
  const title = (c.title ?? '').trim();
  return title.length > 0 ? title : 'Untitled chat';
}

export function Sidebar(props: SidebarProps) {
  const {
    activeThreadKey,
    onNewChat,
    onResumeSession,
    onOpenSettings,
    onSignOut,
    collapsed = false,
    onToggleCollapsed,
    mobile = false,
    onClose,
    width = 268,
    onResize,
    status = 'online',
    refreshTick = 0,
    onConversationDeleted,
  } = props;

  const account = useAccount();
  const getToken = useToken();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [query, setQuery] = useState('');

  // ---- Data fetch: conversations (mount + when active thread changes + debounced search) ----
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const rows = await listConversations(await getToken(), query.trim() || undefined);
        if (!cancelled) setConversations(rows);
      } catch {
        // Non-fatal — empty state shows "No chats yet" / "No chats match" instead.
        if (!cancelled) setConversations([]);
      }
    };
    if (!query.trim()) {
      fetch();
    } else {
      const t = setTimeout(fetch, SEARCH_DEBOUNCE_MS);
      return () => { cancelled = true; clearTimeout(t); };
    }
    return () => { cancelled = true; };
  }, [getToken, query, activeThreadKey, refreshTick]);

  // ---- Resize handle (desktop only) ----
  const resizeStartX = useRef<number | null>(null);
  const startWidth = useRef<number>(width);
  const onResizePointerDown = (e: PointerEvent) => {
    if (mobile || !onResize) return;
    resizeStartX.current = e.clientX;
    startWidth.current = width;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onResizePointerMove = (e: PointerEvent) => {
    if (resizeStartX.current == null || !onResize) return;
    const delta = e.clientX - resizeStartX.current;
    const next = Math.max(220, Math.min(480, startWidth.current + delta));
    onResize(next);
  };
  const onResizePointerUp = (e: PointerEvent) => {
    resizeStartX.current = null;
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* no-op */ }
  };

  // ---- Conversation CRUD ----
  const onDeleteConversation = useCallback(async (threadKey: string, e: MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteConversation(await getToken(), threadKey);
      setConversations(prev => prev.filter(c => c.threadKey !== threadKey));
      // Tell the parent so it can clear the main pane if the deleted
      // thread is the one currently being displayed. Always fire — the
      // parent compares against its own active threadKey and no-ops when
      // it isn't a match.
      onConversationDeleted?.(threadKey);
    } catch { /* no-op */ }
  }, [getToken, onConversationDeleted]);

  // ---- Collapsed rail (icon-only) ----
  // Mock has 4 items vertically: brand · green plus · magnifier · expand
  // chevron. The `.primary` class flips the new-chat button back to the green
  // success pill (the rail's own button rule otherwise wipes the background
  // to transparent because it wants every other rail button to be a quiet
  // ghost). The magnifier expands the sidebar so the user can type into
  // the live search field — there's nowhere to render a real input in 56px.
  if (collapsed && !mobile) {
    return (
      <aside className="shell-side collapsed" aria-label="Chats">
        <div className="side-rail">
          <div className="side-brand" aria-hidden="true">
            <img src="/kairos-icon.png" alt="" width={28} height={28} style={{ borderRadius: 6 }} />
          </div>
          <button type="button" className="primary" onClick={onNewChat} aria-label="New chat" title="New chat">
            <Icon name="i-plus" size={16} />
          </button>
          <button type="button" onClick={onToggleCollapsed} aria-label="Search chats" title="Search chats">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
          <button type="button" onClick={onToggleCollapsed} aria-label="Expand sidebar" title="Expand">
            <Icon name="i-chevron-right" size={14} />
          </button>
        </div>
      </aside>
    );
  }

  // Every conversation is listed, titled or not. The server now always
  // supplies a title (a fallback from the first question), but an untitled
  // row still renders under a placeholder: hiding untitled rows is what made
  // chats whose title generation timed out vanish from history for good.
  const groups = groupConversations(conversations);
  const initials = (account?.name ?? account?.username ?? 'JW')
    .split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <aside
      className="shell-side"
      aria-label="Chats"
      style={!mobile ? { width } : undefined}
    >
      {/* Header — brand + status + new chat + collapse / close */}
      <div className="side-head">
        <div className="side-brand">
          <img src="/kairos-icon.png" alt="" width={28} height={28} style={{ borderRadius: 6 }} />
          <div>
            <div className="side-brand-name">Kairos</div>
            <div className={`status ${status === 'online' ? '' : status}`}>
              {status === 'online' ? 'Online' : status === 'rate-limited' ? 'Rate-limited' : 'Paused'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="side-new-btn" onClick={onNewChat} aria-label="New chat" title="New chat">
            <span className="side-new-icon"><Icon name="i-plus" size={16} /></span>
          </button>
          {mobile ? (
            <button type="button" className="side-collapse-btn" onClick={onClose} aria-label="Close sidebar" title="Close">
              <Icon name="i-chevron-left" size={14} />
            </button>
          ) : (
            <button type="button" className="side-collapse-btn" onClick={onToggleCollapsed} aria-label="Collapse sidebar" title="Collapse">
              <Icon name="i-chevron-left" size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="side-search">
        {/* The CSS positions any inline SVG inside .side-search absolutely
            on the left — supplying our standard magnifier sprite gives the
            input that prefix-icon look (no need to override padding). */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chats"
          aria-label="Search chats"
        />
      </div>

      {/* Sessions scroll region — chat history only (iter-2 removed the
          duplicate saved-prompts section that used to live above this). */}
      <div className="side-sessions">
        {/* Empty states — distinct copy for "never had a chat" vs
            "search returned nothing". Both use the same outer container
            for layout; only the no-history case shows the bubble icon. */}
        {groups.length === 0 && !query.trim() && (
          <div className="side-empty">
            <div className="side-empty__icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div className="side-empty__title">No chats yet</div>
            <div className="side-empty__msg">Your conversations will appear here.</div>
          </div>
        )}

        {groups.length === 0 && query.trim() && (
          <div className="side-empty">
            <div className="side-empty__msg">No chats match &ldquo;{query.trim()}&rdquo;.</div>
          </div>
        )}

        {groups.map(group => (
          <div key={group.label}>
            <div className="side-group-label">{group.label.toUpperCase()}</div>
            {group.items.map(c => (
              <div
                key={c.threadKey}
                className={`session${c.threadKey === activeThreadKey ? ' active' : ''}`}
                onClick={() => onResumeSession(c.threadKey)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onResumeSession(c.threadKey); }}
              >
                <span className="session-text">{sessionLabel(c)}</span>
                <button
                  type="button"
                  className="session-trash"
                  onClick={(e) => onDeleteConversation(c.threadKey, e)}
                  aria-label={`Delete conversation ${sessionLabel(c)}`}
                  title="Delete"
                >
                  <Icon name="i-trash" size={12} />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer — avatar + name + settings + sign-out */}
      <div className="side-foot">
        <div className="side-avatar" aria-hidden="true">{initials}</div>
        <div className="side-foot-name">{account?.name ?? account?.username ?? 'You'}</div>
        <button type="button" className="side-foot-btn" onClick={onOpenSettings} aria-label="Settings" title="Settings">
          <Icon name="i-sliders" size={14} />
        </button>
        <button type="button" className="side-foot-btn" onClick={onSignOut} aria-label="Sign out" title="Sign out">
          <Icon name="i-logout" size={14} />
        </button>
      </div>

      {/* Resize handle (desktop only) */}
      {!mobile && (
        <div
          className="side-resize"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
          aria-hidden="true"
        />
      )}
    </aside>
  );
}
